import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const CATEGORIES = ["Food", "Transport", "Education", "Clothing", "Healthcare", "Housing", "Emergency", "Other"];

export default function PostNeed() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", category: "", location: "", description: "", urgency: "medium",
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    toast.success("Your need has been posted — the community will respond soon.");
    setTimeout(() => navigate("/dashboard"), 800);
  };

  return (
    <>
      <PageHeader
        eyebrow="Ask for help"
        title="Post a need"
        description="Share what you need. Nearby neighbors and matched helpers will respond within hours."
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <Card className="rounded-3xl border-border/60 p-8 sm:p-10 shadow-none">
          <form onSubmit={submit} className="space-y-6">
            <div>
              <Label htmlFor="title" className="font-medium">Title *</Label>
              <Input
                id="title"
                data-testid="need-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Groceries for elderly neighbor"
                className="mt-2 h-12 rounded-xl"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="font-medium">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="need-category-select" className="mt-2 h-12 rounded-xl">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="location" className="font-medium">Location</Label>
                <Input
                  id="location"
                  data-testid="need-location-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Brooklyn, NY"
                  className="mt-2 h-12 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label className="font-medium">Urgency</Label>
              <RadioGroup
                value={form.urgency}
                onValueChange={(v) => setForm({ ...form, urgency: v })}
                className="mt-3 grid grid-cols-3 gap-3"
              >
                {[
                  { v: "low", l: "Flexible", d: "Within weeks" },
                  { v: "medium", l: "Soon", d: "This week" },
                  { v: "high", l: "Urgent", d: "Today / ASAP" },
                ].map(o => (
                  <label
                    key={o.v}
                    data-testid={`urgency-${o.v}`}
                    className={`rounded-2xl border-2 p-4 cursor-pointer transition-all ${
                      form.urgency === o.v ? "border-primary bg-primary/5" : "border-border hover:border-border/80"
                    }`}
                  >
                    <RadioGroupItem value={o.v} className="sr-only" />
                    <div className="font-semibold">{o.l}</div>
                    <div className="text-xs text-muted-foreground mt-1">{o.d}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="desc" className="font-medium">Description *</Label>
              <Textarea
                id="desc"
                data-testid="need-desc-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Share a few details so the right people can help..."
                className="mt-2 rounded-xl min-h-32"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                data-testid="need-submit-btn"
                className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 shadow-sm hover:-translate-y-0.5 transition-all"
              >
                Post my need
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                data-testid="need-cancel-btn"
                className="rounded-full h-12 px-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}
